import { type FormEvent, useState } from "react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";

import { useTranslations } from "@/i18n";
import { theme } from "@/ui/theme";

import { setStoredRoom } from "./roomStorage";

// ── Styled ───────────────────────────────────────────────────

const Shell = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.gradients.floodlight};
  padding: 1.5rem;
`;

const Card = styled(motion.div)`
  width: 100%;
  max-width: 380px;
  border-radius: ${theme.radii["3xl"]};
  border: 1px solid ${theme.colors.border};
  background: color-mix(in oklab, ${theme.colors.bgCard} 75%, transparent);
  backdrop-filter: blur(16px);
  box-shadow: ${theme.shadow.card};
  padding: 2.5rem 2rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
`;

const Logo = styled.div`
  font-size: 2.5rem;
  line-height: 1;
`;

const Title = styled.h1`
  font-family: ${theme.fonts.display};
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  text-align: center;
  color: ${theme.colors.text};
`;

const Subtitle = styled.p`
  font-size: 0.8125rem;
  color: ${theme.colors.textMuted};
  text-align: center;
  line-height: 1.5;
  margin-top: -0.75rem;
`;

const Input = styled.input`
  all: unset;
  width: 100%;
  padding: 0.875rem 1rem;
  border-radius: ${theme.radii.xl};
  border: 1px solid ${theme.colors.border};
  background: color-mix(in oklab, ${theme.colors.bgInput} 70%, transparent);
  font-family: ${theme.fonts.display};
  font-size: 1.125rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-align: center;
  color: ${theme.colors.text};
  box-sizing: border-box;
  transition: border-color 0.15s;

  &::placeholder {
    color: ${theme.colors.textDim};
    font-weight: 400;
    letter-spacing: 0;
  }

  &:focus {
    border-color: ${theme.colors.borderFocus};
  }
`;

const JoinBtn = styled.button`
  all: unset;
  cursor: pointer;
  width: 100%;
  padding: 0.875rem;
  border-radius: ${theme.radii.xl};
  background: ${theme.colors.primary};
  color: ${theme.colors.primaryFg};
  font-family: ${theme.fonts.display};
  font-size: 1rem;
  font-weight: 700;
  text-align: center;
  box-sizing: border-box;
  transition: background 0.15s;

  &:hover {
    background: ${theme.colors.primaryHover};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// ── Component ────────────────────────────────────────────────

interface RoomPickerProps {
  onJoin: (room: string) => void;
}

export const RoomPicker = ({ onJoin }: RoomPickerProps) => {
  const [code, setCode] = useState("");

  const slug = code
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-|-$/g, "");

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!slug) return;
    setStoredRoom(slug);
    onJoin(slug);
  };

  const { t } = useTranslations();

  return (
    <Shell>
      <Card
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        <Logo>🏟️</Logo>
        <Title>{t("general.brandName")}</Title>
        <Subtitle>{t("room.subtitle")}</Subtitle>

        <form
          onSubmit={handleSubmit}
          style={{ width: "100%", display: "flex", flexDirection: "column", gap: "0.75rem" }}
        >
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t("room.placeholder")}
            autoFocus
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
          />
          <JoinBtn type="submit" disabled={!slug}>
            {t("room.join")}
          </JoinBtn>
        </form>
      </Card>
    </Shell>
  );
};
